/**
 * useLanguageVersions
 *
 * Shared state machine for multi-language profile forms. Every profile form in
 * account-ui edits N language variants of the same record (one per
 * `language_code`). This hook manages:
 *
 *  - the working copy of all loaded versions (deep-copied from props so
 *    upstream arrays are never mutated)
 *  - which version is currently active in the editor
 *  - adding a new language locally (not yet persisted — flagged `isNew`)
 *  - removing a language locally
 *  - shallow-merging patches into the active version's domain payload
 *  - per-version dirty tracking since last load/save
 *  - a `markSaved` reset after a successful mutation
 *  - a `tabOptions` array shaped for `<LanguageTabBar />` (English first, then
 *    the rest alphabetical by label)
 *
 * Mirrors the pattern in
 * `profiles-mock/src/pages/profiles/FirmProfileForm.tsx:116-183`, adapted for
 * a generic payload `T` so every form (Firm / Lawyer / Barrister / Clerk /
 * Editorial …) can share one implementation.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type LanguageOption = { code: string; label: string };

export type LanguageVersion<T> = T & {
  languageCode: string;
  /** true while the version exists only on the client (not yet saved server-side) */
  isNew?: boolean;
  /** true if fields have diverged from the server-known snapshot since last load/save */
  isDirty?: boolean;
};

export type UseLanguageVersionsArgs<T> = {
  /** Persisted versions from the API. Passed in as a stable array — hook re-syncs when it changes. */
  initial: LanguageVersion<T>[];
  /** Factory for a fresh version's T payload when the user adds a new language */
  createEmpty: (languageCode: string) => T;
  /** Optional: initial active languageCode. Defaults to the first version, or "en" if empty. */
  defaultActiveCode?: string;
};

export type UseLanguageVersionsReturn<T> = {
  versions: LanguageVersion<T>[];
  activeCode: string;
  setActiveCode: (code: string) => void;
  activeVersion: LanguageVersion<T> | undefined;
  /** Update fields on the active version. Shallow merge into the version's T payload. */
  patchActive: (patch: Partial<T>) => void;
  /** Rename the active version's languageCode in place. No-op if another version already uses `code`. */
  renameActiveLanguage: (code: string) => void;
  /** Add a new language version locally (isNew=true, isDirty=true) and switch to it */
  addLanguage: (code: string) => void;
  /** Remove a version locally. If the removed one was active, switch to the first remaining. */
  removeLanguage: (code: string) => void;
  /** After a successful save, call this to mark versions clean and drop `isNew` flags */
  markSaved: (savedSnapshot: LanguageVersion<T>[]) => void;
  /** Convenience: list of LanguageOption matching the order LanguageTabBar expects */
  tabOptions: LanguageOption[];
  /** Which language codes currently have unsaved edits */
  dirtyCodes: string[];
};

/**
 * Static label map for the most common ISO 639-1 codes used by Legal500
 * profiles. Fall back to `code.toUpperCase()` for anything unlisted.
 * Exported so consumers (e.g. the "add language" dropdown) can reuse it.
 */
export const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  de: "German",
  fr: "French",
  zh: "Mandarin",
};

export function labelForLanguage(code: string): string {
  return LANGUAGE_LABELS[code] ?? code.toUpperCase();
}

function deepCopy<T>(value: T): T {
  // `structuredClone` is available in every browser we target and in Node >=17.
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

function sortVersions<T>(versions: LanguageVersion<T>[]): LanguageVersion<T>[] {
  return [...versions].sort((a, b) => {
    if (a.languageCode === b.languageCode) {
      return 0;
    }
    if (a.languageCode === "en") {
      return -1;
    }
    if (b.languageCode === "en") {
      return 1;
    }
    return labelForLanguage(a.languageCode).localeCompare(labelForLanguage(b.languageCode));
  });
}

export function useLanguageVersions<T>(
  args: UseLanguageVersionsArgs<T>,
): UseLanguageVersionsReturn<T> {
  const { initial, createEmpty, defaultActiveCode } = args;

  const [versions, setVersions] = useState<LanguageVersion<T>[]>(() =>
    sortVersions(initial.map(v => deepCopy(v))),
  );

  const [activeCode, setActiveCodeState] = useState<string>(() => {
    if (defaultActiveCode && initial.some(v => v.languageCode === defaultActiveCode)) {
      return defaultActiveCode;
    }
    if (initial.length > 0) {
      return initial[0].languageCode;
    }
    return defaultActiveCode ?? "en";
  });

  // Re-sync when the upstream `initial` reference changes (e.g. after a refetch).
  // We use a ref to hold the last-seen identity so a parent re-render with the
  // same array doesn't blow away local edits.
  const lastInitialRef = useRef(initial);
  useEffect(() => {
    if (lastInitialRef.current === initial) {
      return;
    }
    lastInitialRef.current = initial;
    const fresh = sortVersions(initial.map(v => deepCopy(v)));
    setVersions(fresh);
    setActiveCodeState(prev => {
      if (fresh.some(v => v.languageCode === prev)) {
        return prev;
      }
      if (defaultActiveCode && fresh.some(v => v.languageCode === defaultActiveCode)) {
        return defaultActiveCode;
      }
      return fresh[0]?.languageCode ?? defaultActiveCode ?? "en";
    });
  }, [initial, defaultActiveCode]);

  const setActiveCode = useCallback((code: string) => {
    setActiveCodeState(code);
  }, []);

  const patchActive = useCallback(
    (patch: Partial<T>) => {
      setVersions(prev =>
        prev.map(v =>
          v.languageCode === activeCode
            ? ({ ...v, ...patch, isDirty: true } as LanguageVersion<T>)
            : v,
        ),
      );
    },
    [activeCode],
  );

  const renameActiveLanguage = useCallback(
    (code: string) => {
      setVersions(prev => {
        if (prev.some(v => v.languageCode === code)) {
          return prev;
        }
        return sortVersions(
          prev.map(v =>
            v.languageCode === activeCode
              ? ({ ...v, languageCode: code, isDirty: true } as LanguageVersion<T>)
              : v,
          ),
        );
      });
      setActiveCodeState(prev => (prev === activeCode ? code : prev));
    },
    [activeCode],
  );

  const addLanguage = useCallback(
    (code: string) => {
      setVersions(prev => {
        if (prev.some(v => v.languageCode === code)) {
          return prev;
        }
        const created: LanguageVersion<T> = {
          ...createEmpty(code),
          languageCode: code,
          isNew: true,
          isDirty: true,
        } as LanguageVersion<T>;
        return sortVersions([...prev, created]);
      });
      setActiveCodeState(code);
    },
    [createEmpty],
  );

  const removeLanguage = useCallback(
    (code: string) => {
      setVersions(prev => {
        const next = prev.filter(v => v.languageCode !== code);
        if (code === activeCode) {
          const fallback = next[0]?.languageCode;
          if (fallback) {
            setActiveCodeState(fallback);
          }
        }
        return next;
      });
    },
    [activeCode],
  );

  const markSaved = useCallback((savedSnapshot: LanguageVersion<T>[]) => {
    const clean = sortVersions(
      savedSnapshot.map(v => {
        const copy = deepCopy(v);
        delete (copy as Partial<LanguageVersion<T>>).isNew;
        delete (copy as Partial<LanguageVersion<T>>).isDirty;
        return copy;
      }),
    );
    setVersions(clean);
    setActiveCodeState(prev => {
      if (clean.some(v => v.languageCode === prev)) {
        return prev;
      }
      return clean[0]?.languageCode ?? prev;
    });
  }, []);

  const activeVersion = useMemo(
    () => versions.find(v => v.languageCode === activeCode),
    [versions, activeCode],
  );

  const tabOptions = useMemo<LanguageOption[]>(
    () =>
      versions.map(v => ({
        code: v.languageCode,
        label: labelForLanguage(v.languageCode),
      })),
    [versions],
  );

  const dirtyCodes = useMemo(
    () => versions.filter(v => v.isDirty || v.isNew).map(v => v.languageCode),
    [versions],
  );

  return {
    versions,
    activeCode,
    setActiveCode,
    activeVersion,
    patchActive,
    renameActiveLanguage,
    addLanguage,
    removeLanguage,
    markSaved,
    tabOptions,
    dirtyCodes,
  };
}

/**
 * useLanguageHotkeys
 *
 * Wires `⌘/Ctrl + ←/→` to cycle through language tabs on the active form.
 * - Wraps around at edges (last → first, first → last).
 * - No-ops when only one language version exists.
 * - Calls `event.preventDefault()` to avoid the browser's history navigation
 *   binding on `⌘/Ctrl + ←/→`.
 * - Skipped when focus is in a contenteditable host (rare in profile forms,
 *   but kept defensive).
 */
export function useLanguageHotkeys(args: {
  codes: string[];
  activeCode: string;
  setActiveCode: (code: string) => void;
  /** Defaults to true. Pass false to disable the listener temporarily. */
  enabled?: boolean;
}): void {
  const { codes, activeCode, setActiveCode, enabled = true } = args;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (codes.length < 2) {
      return;
    }

    const handler = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) {
        return;
      }
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }

      // Don't intercept when the user is editing a contenteditable host —
      // they may rely on native caret-jump bindings there.
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable) {
        return;
      }

      const currentIndex = codes.indexOf(activeCode);
      if (currentIndex === -1) {
        return;
      }
      const direction = event.key === "ArrowRight" ? 1 : -1;
      const nextIndex = (currentIndex + direction + codes.length) % codes.length;
      const nextCode = codes[nextIndex];
      if (!nextCode || nextCode === activeCode) {
        return;
      }
      event.preventDefault();
      setActiveCode(nextCode);
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [codes, activeCode, setActiveCode, enabled]);
}
